import { useEffect, useRef } from 'react';

import { format } from 'date-fns';
import isNil from 'lodash/isNil';

import { DATE_FORMAT } from '@app/consts';
import { useNocSmPageVisitEvent } from '@app/hooks/sales-manago';
import { accommodationSlice, accommodationStaticSlice, useAppSelector } from '@app/state';

import { IPageVisitPayload } from '@noc-sm-plugin';

export const useNocSmAccommodationPageVisit = () => {
   const filters = useAppSelector(accommodationSlice.selectors.selectFilters);
   const accommodation = useAppSelector(accommodationStaticSlice.selectors.selectAccommodation);
   const bestOffer = useAppSelector(accommodationSlice.selectors.selectBestOffer);
   const eventPayload = useRef<IPageVisitPayload | undefined>();
   const completed = useRef<boolean>(false);
   const { pushPageVisitEvent } = useNocSmPageVisitEvent();

    /**
     * Track changes in filters and prepare correct payload
     */
   useEffect(() => {
       if(isNil(filters) || isNil(accommodation)){
           return;
       }

       const { guestsCount, childrenCount,checkIn, checkOut, id } = filters;
       const { type, location, features:{ internalAmenityGroups } } = accommodation;

       eventPayload.current = {
           products: id?.replace(/^\D+/g, ''),
           value: `${bestOffer?.totalPrice ?? ''}`,
           /**
            * Based on business requirements array order should be:
            *
            * 1) detail[1] - event name (“product_view”)
            * 2) detail[2] - accommodation city
            * 3) detail[3] - accommodation region (e.g. seaside, Mazury, Tatry etc. - all applicable)
            * 4) detail[4] - accommodation category
            * 5) detail[5] - stay dates (“DD-MONTH-YYYY - DD-MONTH-YYYY” format)
            * 6) detail[6] - guests (“X_adults, Y_children” format)
            * 7) detail[7] - top amenities
            */
           details:[
               'product_view',
                location?.settlement?.name,
               (location?.regions ?? []).map(region=>region.name).join(', '),
               `${type}`,
               !isNil(checkIn) && !isNil(checkOut) ?
                   `${format(new Date(checkIn ?? Date.now()),DATE_FORMAT.salesManagoEventDateFormat)} - ${format(new Date(checkOut || Date.now()),DATE_FORMAT.salesManagoEventDateFormat)}`
                   : '',
               `${guestsCount}_adults, ${childrenCount}_children`,
               (internalAmenityGroups ?? []).map(group=>group.label).join(', ')
           ]
       };

   }, [filters, accommodation, bestOffer]);

    /**
     * Send event to NocSmPlugin on a component unmounted
     */
   useEffect(() => {
       const handler = ()=>{
           if (!isNil(eventPayload.current) && !completed.current) {
               completed.current = true;
               pushPageVisitEvent(eventPayload.current);
           }
       };

       window.addEventListener('beforeunload', handler)

       return () => {
           window.removeEventListener('beforeunload', handler);
           handler();
       }
   }, []);
};